import {
  AdminEnterpriseModel,
  AdminEnterprisesCollection,
  AdminUserEnterprisesCollection,
  BidsCollection,
  CartItemsCollection,
  CartsCollection,
  CommentFeedsCollection,
  CommentsCollection,
  ContestComment,
  ContestCommentsCollection,
  ContestCommentType,
  ContestsCollection,
  countries,
  CountryCode,
  Education,
  EducationsCollection,
  Experience,
  ExperiencesCollection,
  GroupMember,
  GroupMembersCollection,
  MembershipSubscription,
  MembershipSubscriptionCollection,
  MessagesCollection,
  MilestoneDraftsCollection,
  MilestoneRequestsCollection,
  OnBehalfProjectsCollection,
  OnlineOfflineCollection,
  ParentContestComment,
  PaymentShareMember,
  PaymentShareMembersCollection,
  PaymentShareTeamsCollection,
  ProjectCollaboration,
  ProjectCollaborationsCollection,
  ProjectTitleEditRequest,
  ProjectTitleEditRequestCollection,
  ProjectViewBidsCollection,
  ProjectViewProjectsCollection,
  ReactionsTypeCountCollection,
  ReplyContestComment,
  Review,
  ReviewsCollection,
  ThreadsCollection,
  TimeTrackingSessionCollection,
  transformReactionTypeCount,
  universities,
  UserEnterpriseModel,
  UserInteractionsCollection,
  UserNpsCollection,
  UserSkillsCollection,
  UserTypeInfoCollection,
} from '@freelancer/datastore/collections';
import { PushDocumentType } from '@freelancer/datastore/core';
import { generateId } from '@freelancer/datastore/testing';
import { PartialBy } from '@freelancer/types';
import { toNumber } from '@freelancer/utils';
import { ContestStatusApi } from 'api-typings/contests/contests';
import { GroupMemberRoleApi } from 'api-typings/groups/groups';
import {
  ProjectCollaborationStatusApi,
  ProjectStatusApi,
  ProjectTitleEditRequestStatusApi,
} from 'api-typings/projects/projects';
import { addPushTransformer } from './document-creators';

export function addPushTransformers(authUid: number) {
  addPushTransformer<BidsCollection>('bids', addGeneratedNumericalId);
  addPushTransformer<CartItemsCollection>('cartItems', addGeneratedNumericalId);
  addPushTransformer<CartsCollection>('carts', addGeneratedNumericalId);
  addPushTransformer<ContestsCollection>('contests', addContestComputedFields);
  addPushTransformer<ContestCommentsCollection>(
    'contestComments',
    addContestCommentComputedFields,
  );
  addPushTransformer<EducationsCollection>(
    'educations',
    addEducationComputedFields,
  );
  addPushTransformer<ExperiencesCollection>(
    'experiences',
    addExperienceComputedFields,
  );
  addPushTransformer<MembershipSubscriptionCollection>(
    'membershipSubscription',
    addMembershipSubscriptionComputedFields,
  );
  addPushTransformer<MessagesCollection>('messages', addGeneratedNumericalId);
  addPushTransformer<MilestoneDraftsCollection>(
    'milestoneDrafts',
    addGeneratedNumericalId,
  );
  addPushTransformer<MilestoneRequestsCollection>(
    'milestoneRequests',
    addGeneratedNumericalId,
  );
  addPushTransformer<OnBehalfProjectsCollection>(
    'onBehalfProjects',
    addGeneratedNumericalId,
  );
  addPushTransformer<ProjectViewBidsCollection>(
    'projectViewBids',
    addGeneratedNumericalId,
  );
  addPushTransformer<ProjectViewProjectsCollection>(
    'projectViewProjects',
    addProjectViewProjectComputedFields,
  );
  addPushTransformer<ProjectTitleEditRequestCollection>(
    'projectTitleEditRequest',
    addProjectTitleEditRequestComputedFields,
  );
  addPushTransformer<ReviewsCollection>('reviews', addGeneratedStringId);
  addPushTransformer<ThreadsCollection>('threads', addGeneratedNumericalId);
  addPushTransformer<TimeTrackingSessionCollection>(
    'timeTrackingSession',
    addGeneratedNumericalId,
  );
  addPushTransformer<UserInteractionsCollection>(
    'userInteractions',
    addGeneratedNumericalId,
  );
  addPushTransformer<UserNpsCollection>('userNps', addGeneratedNumericalId);
  addPushTransformer<UserSkillsCollection>(
    'userSkills',
    addGeneratedNumericalId,
  );
  addPushTransformer<UserTypeInfoCollection>(
    'userTypeInfo',
    addGeneratedNumericalId,
  );
  addPushTransformer<CommentFeedsCollection>(
    'commentFeeds',
    addGeneratedNumericalId,
  );
  addPushTransformer<CommentsCollection>('comments', addGeneratedNumericalId);
  // the onlineOffline "id" is a user id which should be provided
  addPushTransformer<OnlineOfflineCollection>('onlineOffline', noop);
  addPushTransformer<GroupMembersCollection>('groupMembers', addGroupMember);
  addPushTransformer<ReactionsTypeCountCollection>(
    'reactionsTypeCount',
    (authId, document) => transformReactionTypeCount(document),
  );
  addPushTransformer<PaymentShareTeamsCollection>(
    'paymentShareTeams',
    addGeneratedNumericalId,
  );
  addPushTransformer<PaymentShareMembersCollection>(
    'paymentShareMembers',
    addPaymentShareMemberComputedFields,
  );
  addPushTransformer<AdminEnterprisesCollection>(
    'adminEnterprises',
    addAdminEnterprisesComputedFields,
  );
  addPushTransformer<AdminUserEnterprisesCollection>(
    'adminUserEnterprises',
    addAdminUserEnterprisesComputedFields,
  );
}

export function noop<T extends { readonly id: number }>(
  authUid: number,
  document: T,
): T {
  return document;
}

export function addContestComputedFields(
  authUid: number,
  document: PushDocumentType<ContestsCollection> &
    Partial<Pick<ContestsCollection['DocumentType'], 'id'>>,
) {
  const id = toNumber(document.id) ?? generateId();

  return {
    ...document,
    id,
    seoUrl: `contest/${id}`,
    status: ContestStatusApi.ACTIVE,
  };
}

export function addGeneratedNumericalId<T extends { readonly id: number }>(
  authUid: number,
  document: PartialBy<T, 'id'>,
): T {
  return { ...document, id: toNumber(document.id) ?? generateId() } as any;
}

export function addGeneratedStringId<T extends { readonly id: string }>(
  authUid: number,
  document: PartialBy<T, 'id'>,
): T {
  return {
    ...document,
    id: document.id ? document.id.toString() : generateId().toString(),
  } as any;
}

export function addProjectViewProjectComputedFields(
  authUid: number,
  document: PushDocumentType<ProjectViewProjectsCollection> &
    Partial<Pick<ProjectViewProjectsCollection['DocumentType'], 'id'>>,
) {
  const id = toNumber(document.id) ?? generateId();

  return {
    ...document,
    id,
    attachments: [],
    bidStats: { bidCount: 0 },
    location: {},
    deleted: false,
    hideBids: false,
    ownerId: authUid,
    previewDescription: '',
    projectCollaborations: [],
    qualifications: [],
    seoUrl: `project-${id}`,
    status: ProjectStatusApi.ACTIVE,
    timeSubmitted: Date.now(),
    selectedBids: [],
    canPostReview: {},
    isEscrowProject: false,
    isSellerKycRequired: false,
    isBuyerKycRequired: false,
    isDeloitteProject: true,
    isTokenProject: true,
    isInsourceProject: false,

    hireme: document.hireme ?? false,
    local: document.local ?? false,
    language: document.language ?? 'en',
    ndaDetails: document.ndaDetails ?? { signatures: [] },
  };
}

export function addProjectCollaborationComputedFields(
  authUid: number,
  document: PushDocumentType<ProjectCollaborationsCollection>,
  collaboratorId: number,
): ProjectCollaboration {
  return {
    ...document,
    id: generateId(),
    userId: collaboratorId,
    contextOwnerId: authUid,
    timeCreated: Date.now(),
    status: ProjectCollaborationStatusApi.ACTIVE,
  };
}

export function addExperienceComputedFields(
  authUid: number,
  document: PushDocumentType<ExperiencesCollection>,
): Experience {
  return { ...document, id: generateId(), userId: authUid, ordering: 1 };
}

export function addEducationComputedFields(
  authUid: number,
  document: PushDocumentType<EducationsCollection>,
): Education {
  const { countryCode, schoolId, startDate, endDate } = document;
  return {
    ...document,
    id: generateId(),
    schoolName:
      countryCode && schoolId
        ? universities[countryCode as CountryCode]?.find(
            uni => uni.id === schoolId,
          )?.name
        : undefined,
    country: countryCode
      ? countries[countryCode as CountryCode].name
      : undefined,
    ordering: 1,
    duration: startDate && endDate ? endDate - startDate : undefined,
  };
}
// Only use this if your test assumes the other party has already left a review.
// It causes Freelancer/EmployerReviewAreaComponent#reviewGiven$ to emit after a review is submitted.
export function addReviewComputedFields(
  authUid: number,
  document: PushDocumentType<ReviewsCollection> &
    Partial<Pick<ReviewsCollection['DocumentType'], 'id'>>,
): Review {
  return {
    ...document,
    id: document.id ? document.id.toString() : generateId().toString(),
    // This is the review context.seoUrl, which differs from the project.seoUrl
    // Matches the reviews datastore call, see FreelancerReviewArea#reviewContext$
    context: {
      ...document.context,
      seoUrl: `/projects/${document.context.seoUrl}/`,
    },
  } as Review;
}

export function addContestCommentComputedFields(
  authUid: number,
  document: PushDocumentType<ContestCommentsCollection>,
): ContestComment {
  if (!document.parentId) {
    return {
      ...document,
      id: generateId(),
      type: ContestCommentType.PARENT,
      parentId: undefined,
      repliesCount: 0,
      timestamp: Date.now(),
      dateLastComment: Date.now(),
    } as ParentContestComment;
  }

  return {
    ...document,
    id: generateId(),
    timestamp: Date.now(),
    dateLastComment: Date.now(),
    type: ContestCommentType.REPLY,
  } as ReplyContestComment;
}

export function addPaymentShareMemberComputedFields(
  authUid: number,
  document: PushDocumentType<PaymentShareMembersCollection>,
): PaymentShareMember {
  return {
    ...document,
    id: generateId(),
    billingAgreementId: generateId(),
  } as PaymentShareMember;
}

export function addGroupMember(
  authUid: number,
  document: PushDocumentType<GroupMembersCollection>,
): GroupMember {
  const id = generateId();
  return {
    ...document,
    id,
    memberId: id,
    role: GroupMemberRoleApi.GUEST,
    created: Date.now(),
    isRemoved: false,
    userId: authUid,
  };
}

export function addAdminEnterprisesComputedFields(
  authUid: number,
  document: PushDocumentType<AdminEnterprisesCollection>,
): AdminEnterpriseModel {
  return {
    ...document,
    id: generateId(),
    membersCount: 0,
    dateCreated: Date.now(),
  };
}

export function addAdminUserEnterprisesComputedFields(
  authUid: number,
  document: PushDocumentType<AdminUserEnterprisesCollection>,
): UserEnterpriseModel {
  return {
    ...document,
    id: generateId(),
    dateAdded: Date.now(),
  };
}

export function addMembershipSubscriptionComputedFields(
  authUid: number,
  document: PushDocumentType<MembershipSubscriptionCollection>,
): MembershipSubscription {
  return {
    ...document,
    id: generateId(),
  };
}

export function addProjectTitleEditRequestComputedFields(
  authUid: number,
  document: PushDocumentType<ProjectTitleEditRequestCollection>,
): ProjectTitleEditRequest {
  return {
    ...document,
    id: generateId(),
    requestedBy: authUid,
    status: ProjectTitleEditRequestStatusApi.PENDING,
  };
}
